import React, { useState, useEffect } from 'react';

const DishDetail = ({ dish, categoryName, currency, onClose, onAddToCart }) => {
  const [selectedOption, setSelectedOption] = useState(null);
  const [selectedAddon, setSelectedAddon] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [showContent, setShowContent] = useState(false);
  const [startPosition, setStartPosition] = useState(null);

  // 确保菜品对象包含所有必要的属性
  const dishData = {
    ...dish,
    description: dish.description || { brief: '', detail: '' },
    allergens: dish.allergens || [],
    ingredients: dish.ingredients || [],
    cookingMethod: dish.cookingMethod || '',
    options: dish.options || [],
    addons: dish.addons || [],
    name: dish.name || '',
    rawName: dish.rawName || '',
    price: dish.price || 0,
    rawPrice: dish.rawPrice || 0
  };

  useEffect(() => {
    // 加载时的动画效果
    const timer = setTimeout(() => {
      setShowContent(true);
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  const handleAddToCart = (e) => {
    // 获取点击位置
    const rect = e.target.getBoundingClientRect();
    const position = {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2
    };
    setStartPosition(position);
    
    // 添加数量参数到购物车
    onAddToCart(dishData, categoryName, selectedOption, selectedAddon, position, quantity);
    onClose();
  };

  // 外部点击时关闭
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className="bg-black bg-opacity-50 fixed inset-0 z-50 flex items-end justify-center sm:items-center"
      onClick={handleOverlayClick}
    >
      <div 
        className={`bg-white rounded-t-2xl sm:rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-hidden transition-all duration-300 transform ${
          showContent ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
        }`}
      >
        {/* 关闭按钮和标题 */}
        <div className="sticky top-0 z-10 bg-white border-b border-gray-100 px-4 py-3 flex justify-between items-center">
          <h2 className="font-semibold text-gray-800 text-lg">{dishData.name}</h2>
          <button 
            onClick={onClose}
            className="backdrop-blur-sm bg-gray-50/80 rounded-full p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100/80 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* 可滚动的内容区域 */}
        <div className="overflow-y-auto p-4" style={{maxHeight: 'calc(90vh - 60px)'}}>
          {/* 菜品描述 */}
          <div className="mb-6">
            <p className="text-gray-500 italic mb-2">{dishData.rawName}</p>
            {dishData.description.brief && (
              <p className="text-gray-600 mb-2">{dishData.description.brief}</p>
            )}
            {dishData.description.detail && (
              <p className="text-gray-600 mb-2">{dishData.description.detail}</p>
            )}
            <div className="text-lg flex justify-between font-semibold text-indigo-600 mt-2">
              <span className="text-lg text-gray-500">
                ¥{dishData.price}
              </span>
              <span className="text-sm text-gray-500">
                {currency} {dishData.rawPrice}
              </span>
            </div>
          </div>

          {/* 过敏原标签 */}
          {dishData.allergens.length > 0 && (
            <div className="space-y-2 pb-2">
              <h4 className="text-sm font-medium text-gray-700 flex items-center">
                <svg className="w-4 h-4 mr-1 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                过敏原
              </h4>
              <div className="flex flex-wrap gap-2">
                {dishData.allergens.map((allergen, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-red-50 text-red-600 rounded-full text-sm border border-red-100"
                  >
                    {allergen}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* 食材标签 */}
          {dishData.ingredients.length > 0 && (
            <div className="space-y-2 pb-2">
              <h4 className="text-sm font-medium text-gray-700 flex items-center">
                <svg className="w-4 h-4 mr-1 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                </svg>
                主要食材
              </h4>
              <div className="flex flex-wrap gap-2">
                {dishData.ingredients.map((ingredient, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-green-50 text-green-600 rounded-full text-sm border border-green-100"
                  >
                    {ingredient}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* 烹饪方法 */}
          {/* {dish.cookingMethod && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-700 flex items-center">
                <svg className="w-4 h-4 mr-1 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                </svg>
                烹饪方法
              </h4>
              <p className="text-gray-600">{dish.cookingMethod}</p>
            </div>
          )} */}

          {/* 规格选择 */}
          {dish.hasOptions && dishData.options.length > 0 && (
            <div className="mb-6">
              <h3 className="font-medium text-gray-700 mb-2">规格</h3>
              <div className="grid grid-cols-2 gap-2">
                {dishData.options.map((option, index) => (
                  <button
                    key={index}
                    className={`py-2 px-3 rounded-lg text-sm border transition-all ${
                      selectedOption === option
                        ? 'bg-indigo-100 border-indigo-300 text-indigo-700'
                        : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                    }`}
                    onClick={() => setSelectedOption(option)}
                  >
                    <div className="flex justify-between items-center">
                      <span>{option.translation_title}</span>
                      <span className="font-medium">¥{option.translation_price}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* 附加项选择 */}
          {dish.hasAddon && dishData.addons.length > 0 && (
            <div className="mb-6">
              <h3 className="font-medium text-gray-700 mb-2">附加项</h3>
              <div className="grid grid-cols-2 gap-2">
                {dishData.addons.map((addon, index) => (
                  <button
                    key={index}
                    className={`py-2 px-3 rounded-lg text-sm border transition-all ${
                      selectedAddon === addon
                        ? 'bg-indigo-100 border-indigo-300 text-indigo-700'
                        : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                    }`}
                    onClick={() => setSelectedAddon(addon)}
                  >
                    <div className="flex justify-between items-center">
                      <span>{addon.raw_title || addon.translation_title}</span>
                      <span className="font-medium">¥{addon.translation_price}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* 数量选择 */}
          <div className="mb-8">
            <h3 className="font-medium text-gray-700 mb-2">数量</h3>
            <div className="flex items-center bg-gray-50 rounded-lg inline-flex">
              <button
                onClick={() => quantity > 1 && setQuantity(quantity - 1)}
                className="w-10 h-10 flex items-center justify-center text-gray-600 hover:text-indigo-600"
                disabled={quantity <= 1}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                </svg>
              </button>
              <span className="w-10 text-center">{quantity}</span>
              <button
                onClick={() => setQuantity(quantity + 1)}
                className="w-10 h-10 flex items-center justify-center text-gray-600 hover:text-indigo-600"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </button>
            </div>
          </div>

          {/* 添加到购物车按钮 */}
          <button
            onClick={handleAddToCart}
            className="w-full bg-indigo-600 text-white py-3 rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center space-x-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <span>加入购物车</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default DishDetail; 